import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchCustomerById } from "../services/customerService";
import { getBrowserTimezone, resolveScheduleTimezone } from "../utils/scheduleTime";
import { readPersistedWorkspace } from "../utils/workspacePersistence";
import { debug } from "../utils/debugLogger";

/**
 * Business-profile timezone cache, keyed by customer id.
 *
 * Module-level on purpose: several schedule pickers can be mounted at once (the
 * post list, a chat post card, the create form) and they should not each fire
 * their own GET for the same customer. A customer's timezone changes about as
 * often as its postal address, so a per-session cache is plenty.
 */
const timezoneCache = new Map<string, string>();
const inFlight = new Map<string, Promise<string | undefined>>();

/** Drop a cached zone so the next read refetches — call after updating a customer. */
export const invalidateScheduleTimezone = (customerId?: string): void => {
  if (customerId) timezoneCache.delete(customerId);
  else timezoneCache.clear();
};

/**
 * Resolve the timezone a schedule picker should default to for `customerId`.
 *
 * Starts with the in-memory cache hit (instant) or "UTC" as a neutral loading
 * state, then upgrades to the business profile's timezone once the API call
 * resolves. The browser's zone is only used as a last-resort when the customer
 * has no timezone at all — it is never the initial visible default, so the
 * picker always ends up showing the customer/brand timezone.
 */
export const useScheduleTimezone = (
  customerId?: string | null
): { timezone: string; isResolved: boolean } => {
  const { idToken } = useAuth();
  const cached = customerId ? timezoneCache.get(customerId) : undefined;

  // Start with the cache hit (best case: no flicker) or a neutral "UTC"
  // placeholder. We never default to getBrowserTimezone() here so the user
  // never sees their own locale flash in before the customer zone loads.
  const [timezone, setTimezone] = useState<string>(
    () => cached ?? "UTC"
  );
  const [isResolved, setIsResolved] = useState<boolean>(Boolean(cached));

  useEffect(() => {
    if (!customerId || !idToken) {
      // No customer selected — fall back to browser timezone as true last resort.
      setTimezone(getBrowserTimezone());
      setIsResolved(true);
      return;
    }

    const hit = timezoneCache.get(customerId);
    if (hit) {
      setTimezone(hit);
      setIsResolved(true);
      return;
    }

    let cancelled = false;

    // Share one request per customer across concurrently-mounted pickers.
    let request = inFlight.get(customerId);
    if (!request) {
      request = fetchCustomerById(customerId, idToken)
        .then((customer) => customer?.timezone)
        .catch((error) => {
          debug.error("Could not load customer timezone", error);
          return undefined;
        })
        .finally(() => {
          inFlight.delete(customerId);
        });
      inFlight.set(customerId, request);
    }

    request.then((tz) => {
      // resolveScheduleTimezone falls back to browser only when the customer
      // has no timezone — the correct last resort, not the initial default.
      const resolved = resolveScheduleTimezone(tz);
      if (tz) timezoneCache.set(customerId, resolved);
      if (cancelled) return;
      setTimezone(resolved);
      setIsResolved(true);
    });

    return () => {
      cancelled = true;
    };
  }, [customerId, idToken]);

  return { timezone, isResolved };
};

/**
 * The active workspace's scheduling timezone, without needing
 * CustomerWorkspaceProvider in the tree.
 *
 * `useCustomerWorkspace` throws outside its provider, and that provider is
 * mounted partway down the tree rather than at the app root — so schedule
 * pickers deep in chat cannot safely depend on it. The persisted workspace is a
 * plain localStorage read and is the same source the provider hydrates from.
 */
export const useActiveScheduleTimezone = (): {
  timezone: string;
  isResolved: boolean;
} => {
  // Read once per mount: cards remount when the workspace changes, and reading
  // localStorage on every render of every card in a long list is wasteful.
  const [customerId] = useState<string | null>(() => readPersistedWorkspace().id);
  return useScheduleTimezone(customerId);
};

/**
 * Imperative variant for non-component code paths that need the zone once.
 * Uses the same cache as the hook.
 */
export const useScheduleTimezoneResolver = (): ((
  customerId: string,
  idToken: string
) => Promise<string>) =>
  useCallback(async (customerId: string, idToken: string) => {
    const hit = timezoneCache.get(customerId);
    if (hit) return hit;
    try {
      const customer = await fetchCustomerById(customerId, idToken);
      const resolved = resolveScheduleTimezone(customer?.timezone);
      if (customer?.timezone) timezoneCache.set(customerId, resolved);
      return resolved;
    } catch (error) {
      debug.error("Could not resolve customer timezone", error);
      return getBrowserTimezone();
    }
  }, []);
